import React, { useEffect, useMemo, useRef, useState } from "react";
import Block from "@mui/icons-material/Block";
import CheckCircle from "@mui/icons-material/CheckCircle";
import CommonTable from "../common/commonTable";
import "../../styles/admin/student.scss";
import StudentForm from "./StudentForm";
import StudentSelectForm from "./StudentSelectForm";
import TeacherAssignForm from "./TeacherAssignForm";
import ResetPasswordPopup from "../common/ResetPasswordPopup";
import {
  createInstitutionStudent,
  deleteInstitutionStudent,
  getAllInstitutionStudent,
  updateInstitutionStudent,
  getAllInstitutionCourse,
  getAllInstitutionCourseBatch,
  getAllInstitutionBatchStudent,
  assignInstitutionBatchStudent,
  getAllBatchNotAssignStudent,
  bulkUploadInstitutionStudent,
  resetInstitutionStudentPassword,
} from "../../helper/apicalls/institution";
import Cookies from "js-cookie";
import { useSearchParams } from "react-router-dom";
import SuccessPopup from "../common/SuccessPopup";
import GlobalLoader from "../common/GlobalLoader";
import Pagination from "../common/Pagination";

const Student = ({ batchInfo = false, batchId, institution_id, type }) => {
  const [students, setStudents] = useState([]);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const authToken = Cookies.get("admin_token");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [batchPage, setBatchPage] = useState(1);
  const [batchLimit, setBatchLimit] = useState(10);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("Student created successfully");
  const [popupType, setPopupType] = useState("success");
  const [loader, setLoader] = useState(false);
  const [institutionCourseData, setInstitutionCourseData] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [isStudentPickerOpen, setIsStudentPickerOpen] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "", grade: "" });
  const searchDebounceRef = useRef(null);
  const [resetPopupOpen, setResetPopupOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);

  const handleResetPasswordClick = (row) => {
    setResetTargetUser(row);
    setResetPopupOpen(true);
  };

  const handleResetPasswordSubmit = async (newPassword) => {
    if (!resetTargetUser) return;
    setLoader(true);
    try {
      const res = await resetInstitutionStudentPassword(authToken, resetTargetUser.id, {
        password: newPassword,
      });
      setSuccessMsg(res?.message || "Password reset successfully");
      setPopupType("success");
      setSuccessOpen(true);
    } catch (err) {
      console.error("Failed to reset password:", err);
      setSuccessMsg(err?.message || "Failed to reset password");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
      setResetPopupOpen(false);
      setResetTargetUser(null);
    }
  };

  const fileInputRef = useRef(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploadResultOpen, setIsUploadResultOpen] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleCopyPassword = (pw) => {
    navigator.clipboard.writeText(pw);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const handleUploadClick = (e) => {
    e.stopPropagation();
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLoader(true);
    try {
      const res = await bulkUploadInstitutionStudent(authToken, file, id);
      await fetchInstitutionStudent();
      setUploadResult(res);
      setIsUploadResultOpen(true);
    } catch (err) {
      console.error("CSV upload failed", err);
      setSuccessMsg(err?.message || "CSV upload failed");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      e.target.value = "";
      setLoader(false);
    }
  };

  const handleDownloadSample = () => {
    const gradeHeader = "grade";
    const sampleGrade1 = type === "college" ? "1st Year" : "6";
    const sampleGrade2 = type === "college" ? "2nd Year" : "7";
    const csvContent = `name,email,register_no,${gradeHeader}\nJohn Doe,john@example.com,REG001,${sampleGrade1}\nJane Smith,jane@example.com,REG002,${sampleGrade2}\n`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "student_bulk_upload_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(val);
    }, 300);
  };

  const columns = useMemo(() => {
    const cols = [
      { field: "name", label: "Name" },
      { field: "email", label: "Email" },
      {
        field: "register_no",
        label: "Register No.",
        align: "center",
        render: (value) => {
          const v = value;
          return v ? v : "-";
        },
      },
    ];

    if (!batchInfo) {
      cols.push(
        {
          field: "grade",
          label: "Grade",
          render: (v) => {
            if (!v) return "-";
            if (v === "1 year") return "1st Year";
            if (v === "2 year") return "2nd Year";
            if (v === "3 year") return "3rd Year";
            if (v === "4 year") return "4th Year";
            return v.charAt(0).toUpperCase() + v.slice(1);
          },
          align: "center",
        },
        {
          field: "batch_name",
          label: "Batch name",
          render: (_value, row) => {
            const name = row?.batch_name;
            return name ? name.charAt(0).toUpperCase() + name.slice(1) : "-";
          },
          align: "center",
        },
        {
          field: "batch_assigned",
          label: "Batch",
          align: "center",
          render: (_value, row) => {
            const value = row?.batch_assigned;

            return (
              <span
                className={`assign-status-badge ${
                  value ? "assign-status-active" : "assign-status-inactive"
                }`}
              >
                {value ? "Assigned" : "Not assigned"}
              </span>
            );
          },
        }
      );
    }

    cols.push({
      field: "disabled",
      label: "Status",
      align: "center",
      render: (isDisabled) => (
        <span
          className={`status-badge ${
            isDisabled ? "status-inactive" : "status-active"
          }`}
        >
          {isDisabled ? "Disabled" : "Active"}
        </span>
      ),
    });

    cols.push({
      field: "reset_password",
      label: "Reset Password",
      align: "center",
      render: (_, row) => (
        <button
          className="reset-password-btn-table"
          onClick={(e) => {
            e.stopPropagation();
            handleResetPasswordClick(row);
          }}

        >
          Reset
        </button>
      ),
    });

    return cols;
  }, [batchInfo]);

  const handleEdit = (id, row) => {
    setCurrentStudent(row);
    setIsFormOpen(true);
  };

  const toggleStatus = async (row) => {
    setLoader(true);
    try {
      const res = await updateInstitutionStudent(authToken, row?.id, {
        disabled: !row?.disabled,
      });
      setSuccessMsg(res?.message || "Student updated successfully");
      setPopupType("success");
      setSuccessOpen(true);
      if (batchInfo) {
        await fetchBatchStudent();
      } else {
        await fetchInstitutionStudent();
      }
    } catch (err) {
      console.error("Failed to toggle status:", err);
      setSuccessMsg(err?.message || "Failed to update status");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };

  const handleOpenAssign = () => {
    if (!selectedStudentIds?.length) return;
    setIsAssignOpen(true);
  };

  const handleAssignClose = () => setIsAssignOpen(false);

  const fetchInstitutionCourse = async () => {
    try {
      const res = await getAllInstitutionCourse(authToken, id);
      setInstitutionCourseData(res[0]?.course || []);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    } finally {
    }
  };

  const fetchCourseBatch = async (key) => {
    setLoader(true);
    try {
      const res = await getAllInstitutionCourseBatch(authToken, id, key);
      setBatchesList(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleAssignSubmit = async ({ studentIds, courseId, batchId }) => {
    try {
      const res = await assignInstitutionBatchStudent(authToken, {
        batch_id: batchId,
        student_ids: studentIds,
      });
      setSuccessMsg(res?.message || "Updated batch student list");
      setPopupType("success");
      setSuccessOpen(true);
      setIsAssignOpen(false);
      setSelectedStudentIds([]);
      if (batchInfo) {
        await fetchBatchStudent();
      } else {
        await fetchInstitutionStudent();
      }
    } catch (err) {
      console.error("Failed to assign students:", err);
      setSuccessMsg(err?.message || "Failed to assign students");
      setPopupType("error");
      setSuccessOpen(true);
    }
  };

  const handleDelete = async (id) => {
    setLoader(true);
    try {
      if (!batchInfo) {
        const deleteStudent = await deleteInstitutionStudent(authToken, id);
        setSuccessMsg(deleteStudent?.message || "Student deleted successfully");
        setPopupType("success");
        setSuccessOpen(true);
        if (batchInfo) {
          await fetchBatchStudent();
        } else {
          await fetchInstitutionStudent();
        }
      } else {
        const res = await assignInstitutionBatchStudent(authToken, {
          batch_id: batchId,
          remove_student_ids: [id],
        });
        setSuccessMsg(res?.message || "Student deleted successfully");
        setPopupType("success");
        setSuccessOpen(true);
        await fetchBatchStudent();
      }
    } catch (err) {
      console.error("Failed to delete student:", err);
      setSuccessMsg(err?.message || "Failed to delete student");
      setPopupType("error");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };

  const handleCreateClick = () => {
    setCurrentStudent(null);
    setIsFormOpen(true);
  };

  const handleAddStudentClick = async () => {
    setIsStudentPickerOpen(true);
    setLoader(true);
    try {
      const allRes = await getAllBatchNotAssignStudent(
        authToken,
        institution_id
      );
      setAvailableStudents(allRes);
    } catch (err) {
      console.error("Failed to load students for selection:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleAddStudentClose = () => setIsStudentPickerOpen(false);

  const handleAddStudentSubmit = async ({ studentIds }) => {
    if (!Array.isArray(studentIds) || studentIds.length === 0) return;
    setLoader(true);
    try {
      const res = await assignInstitutionBatchStudent(authToken, {
        batch_id: batchId,
        student_ids: studentIds,
      });
      setSuccessMsg(res?.message || "Students assigned successfully");
      setSuccessOpen(true);
      setIsStudentPickerOpen(false);
      if (batchInfo) {
        await fetchBatchStudent();
      } else {
        await fetchInstitutionStudent();
      }
    } catch (err) {
      console.error("Failed to assign students:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentStudent(null);
  };

  const handleFormSubmit = async (data) => {
    setLoader(true);
    if (currentStudent) {
      try {
        const studentId = currentStudent?.id ?? currentStudent?._id;
        const res = await updateInstitutionStudent(authToken, studentId, data);
        setSuccessMsg(res?.message || "Student updated successfully");
        setPopupType("success");
        setSuccessOpen(true);
        if (batchInfo) {
          await fetchBatchStudent();
        } else {
          await fetchInstitutionStudent();
        }
        handleFormClose();
      } catch (err) {
        console.error("Failed to update student:", err);
        setSuccessMsg(err?.message || "Failed to update student");
        setPopupType("error");
        setSuccessOpen(true);
      } finally {
        setLoader(false);
      }
    } else {
      try {
        const payload = {
          name: data?.name,
          password: data?.password,
          ...(data?.email !== "" ? { email: data?.email } : {}),
          ...(data?.register_no !== ""
            ? { register_no: data?.register_no }
            : {}),
          grade: data?.grade,
          institution: id,
        };
        const res = await createInstitutionStudent(authToken, payload);
        setSuccessMsg(res?.message || "Student created successfully");
        setPopupType("success");
        setSuccessOpen(true);
        handleFormClose();
        if (batchInfo) {
          await fetchBatchStudent();
        } else {
          await fetchInstitutionStudent();
        }
      } catch (err) {
        console.error("Failed to create student:", err);
        setSuccessMsg(err?.message || "Failed to create student");
        setPopupType("error");
        setSuccessOpen(true);
      } finally {
        setLoader(false);
      }
    }
  };

  const fetchInstitutionStudent = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;
      if (filters?.grade) params.grade = filters.grade;

      const res = await getAllInstitutionStudent(authToken, id, params);
      setStudents(Array.isArray(res?.data) ? res.data : []);
      const tp =
        Number(res?.total_pages) ||
        (Number(res?.total) && Math.ceil(Number(res.total) / limit)) ||
        1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchBatchStudent = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = { page: batchPage, limit: batchLimit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;
      if (filters?.grade) params.grade = filters.grade;

      const res = await getAllInstitutionBatchStudent(
        authToken,
        batchId,
        params
      );
      setStudents(Array.isArray(res?.data) ? res.data : []);
      const tp =
        Number(res?.total_pages) ||
        (Number(res?.total) && Math.ceil(Number(res.total) / limit)) ||
        1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    } finally {
      setLoader(false);
    }
  };

  const goToPage = (p) => {
    const next = Math.max(1, p);
    setLoader(true);
    if (batchInfo) {
      setBatchPage(next);
    } else {
      setPage(next);
    }
  };

  const changePerPage = (pp) => {
    setLoader(true);
    if (batchInfo) {
      setBatchLimit(pp);
      setBatchPage(1);
    } else {
      setLimit(pp);
      setPage(1);
    }
  };

  useEffect(() => {
    if (!batchInfo) {
      fetchInstitutionCourse();
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    if (!batchInfo) {
      fetchInstitutionStudent();
    }
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [id, page, batchInfo, searchTerm, filters, limit]);

  useEffect(() => {
    if (!batchId) return;
    (async () => {
      await fetchBatchStudent();
    })();
  }, [batchId, searchTerm, filters, batchPage, batchLimit]);

  return (
    <div className="student-tab-container">
      {loader && <GlobalLoader visible={loader} text="Loading students..." />}
      <div className="student-tab">
        <CommonTable
          data={students}
          columns={columns}
          getRowId={(r) => r.id}
          onSearchChange={handleSearchChange}
          filterSections={[
            {
              key: "status",
              title: "Status",
              multiple: false,
              options: [
                { value: "", label: "All" },
                { value: "active", label: "Active" },
                { value: "disabled", label: "Disabled" },
              ],
            },
            {
              key: "grade",
              title: "Grade",
              multiple: false,
              options:
                type === "college"
                  ? [
                      { value: "", label: "All" },
                      { value: "1 year", label: "1st Year" },
                      { value: "2 year", label: "2nd Year" },
                      { value: "3 year", label: "3rd Year" },
                      { value: "4 year", label: "4th Year" },
                    ]
                  : [
                      { value: "", label: "All" },
                      { value: "6", label: "6" },
                      { value: "7", label: "7" },
                      { value: "8", label: "8" },
                      { value: "9", label: "9" },
                      { value: "10", label: "10" },
                      { value: "11", label: "11" },
                      { value: "12", label: "12" },
                    ],
            },
          ]}
          filterValues={filters}
          onFilterChange={setFilters}
          showToolbar
          isRowSelectable={(row) => {
            const value = row?.batch_assigned;
            return !value;
          }}
          extraActions={(row) => {
            const items = [];
            if (!batchInfo) {
              if (row.disabled) {
                items.push({
                  key: "enable",
                  label: "Enable",
                  icon: (
                    <CheckCircle
                      fontSize="small"
                      sx={{ color: "var(--color-success)" }}
                    />
                  ),
                  onClick: () => toggleStatus(row),
                });
              } else {
                items.push({
                  key: "disable",
                  label: "Disable",
                  icon: <Block fontSize="small" />,
                  onClick: () => toggleStatus(row),
                  danger: true,
                });
              }
            }
            return items;
          }}
          toolbarRight={
            <>
              {!batchInfo && (
                <>
                  <button
                    className="student-tab-header-btn btn-interactive"
                    onClick={handleCreateClick}
                  >
                    Create
                  </button>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      className="student-bulk-upload-btn"
                      onClick={handleUploadClick}
                      type="button"
                      title="Upload CSV. Expected columns: name, email, register_no"
                    >
                      Upload CSV
                    </button>
                    <button
                      onClick={handleDownloadSample}
                      type="button"
                      style={{
                        fontSize: "var(--fs-sm)",
                        padding: "10px 16px",
                        borderRadius: "6px",
                        fontWeight: "500",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        backgroundColor: "var(--bg, #ffffff)",
                        color: "var(--bulk-upload-bg, #4f46e5)",
                        border: "1px solid var(--bulk-upload-bg, #4f46e5)",
                      }}
                      title="Download sample template CSV"
                    >
                      Download Sample CSV
                    </button>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </div>
                  <button
                    className="student-tab-header-btn btn-interactive"
                    onClick={handleOpenAssign}
                    disabled={
                      !(selectedStudentIds && selectedStudentIds?.length > 0)
                    }
                  >
                    Assign
                  </button>
                </>
              )}
              {batchInfo && (
                <button
                  className="student-tab-header-btn btn-interactive"
                  onClick={handleAddStudentClick}
                >
                  Add Students
                </button>
              )}
            </>
          }
          title={"Students List"}
          multiSelect={!batchInfo}
          selectedRowIds={selectedStudentIds}
          onSelectionChange={setSelectedStudentIds}
          showActions
          fixedBodyRows={5}
          maxBodyHeight={`calc(100vh - 295px)`}
          onEdit={handleEdit}
          showFilter={batchInfo ? false : true}
          onDelete={handleDelete}
          hideEditBtn={true}
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          deleteMessage={
            batchInfo
              ? "Are you sure you want to remove from this batch? This action cannot be undone."
              : "Are you sure you want to delete this item? This action cannot be undone."
          }
          removeActionLabel={batchInfo ? "Remove from Batch" : "Delete"}
          actionLabelDelete={!batchInfo ? "Delete" : "Remove"}
          showEditAction={batchInfo ? false : true}
        />
      </div>
      <div className="student-pagination">
        <Pagination
          page={batchInfo ? batchPage : page}
          pageCount={totalPages}
          onChange={goToPage}
          perPage={batchInfo ? batchLimit : limit}
          perPageOptions={[5, 10, 25, 100]}
          onPerPageChange={changePerPage}
        />
      </div>
      {isAssignOpen && (
        <TeacherAssignForm
          isOpen={isAssignOpen}
          onClose={handleAssignClose}
          onSubmit={handleAssignSubmit}
          selectedIds={selectedStudentIds}
          courses={institutionCourseData}
          batches={batchesList}
          title="Assign Students"
          idKey="studentIds"
          getBatched={fetchCourseBatch}
        />
      )}
      {isFormOpen && (
        <StudentForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={currentStudent}
          type={type}
        />
      )}
      {isStudentPickerOpen && (
        <StudentSelectForm
          isOpen={isStudentPickerOpen}
          onClose={handleAddStudentClose}
          onSubmit={handleAddStudentSubmit}
          students={availableStudents}
          title="Add Students"
        />
      )}
      {successOpen && (
        <SuccessPopup
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          message={successMsg}
          type={popupType}
        />
      )}
      {isUploadResultOpen && uploadResult && (
        <div className="common-popup-overlay">
          <div className="common-popup-container" style={{ maxWidth: "600px" }}>
            <div className="popup-header with-close">
              <h2>Bulk Upload Report</h2>
              <button className="popup-close-button" onClick={() => setIsUploadResultOpen(false)}>&times;</button>
            </div>
            <div className="popup-content bulk-upload-report-content">
              <div className="summary-cards-container">
                <div className="summary-card success">
                  <div className="count">
                    {uploadResult.created}
                  </div>
                  <div className="label">
                    Created Successfully
                  </div>
                </div>
                <div className={`summary-card ${uploadResult.skipped?.length > 0 ? "skipped" : "no-skipped"}`}>
                  <div className="count">
                    {uploadResult.skipped?.length || 0}
                  </div>
                  <div className="label">
                    Rows Skipped
                  </div>
                </div>
              </div>

              <div className="password-notice-banner">
                <div className="banner-left">
                  <span className="banner-text">
                    Default password for newly created students: <strong>student123</strong>
                  </span>
                </div>
                <button className="copy-btn" onClick={() => handleCopyPassword("student123")}>
                  {passwordCopied ? "Copied!" : "Copy"}
                </button>
              </div>
              
              {uploadResult.skipped?.length > 0 && (
                <div className="skipped-details-section">
                  <h4>Skipped Rows Details</h4>
                  <div className="skipped-table-wrapper">
                    <table className="skipped-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Identifier</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.skipped.map((item, idx) => (
                          <tr key={idx}>
                            <td className="row-col">Row {item.row}</td>
                            <td className="identifier-col">
                              {item.email || item.register_no || "-"}
                            </td>
                            <td className="reason-col">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="popup-actions">
              <button className="popup-submit-button" onClick={() => setIsUploadResultOpen(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
      {resetPopupOpen && resetTargetUser && (
        <ResetPasswordPopup
          isOpen={resetPopupOpen}
          onClose={() => {
            setResetPopupOpen(false);
            setResetTargetUser(null);
          }}
          userName={resetTargetUser.name}
          onResetPassword={handleResetPasswordSubmit}
        />
      )}
    </div>
  );
};

export default Student;
